import { useEffect, useMemo, useCallback, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Accordion,
  AccordionItem,
  Button,
  Checkbox,
  IconButton,
  MenuButton,
  MenuItem,
  RadioButton,
  RadioButtonGroup,
  Tabs,
  TabList,
  Tab,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import {
  Add,
  OpenPanelLeft,
  OpenPanelFilledLeft,
  Reset,
  Layers,
  Information,
  View,
  ViewOff,
} from '@carbon/react/icons';

import { SearchPageLayout } from 'components/search-page-layout/search-page-layout';
import { DraggableDivider } from 'components/draggable-divider';
import { MapQueryFilterSidebar } from 'components/map-query-filter-sidebar';
import { DebouncedInput } from 'components/debounced-input';
import {
  AddDocumentSidePanel,
  type DocumentUploadConfig,
} from 'components/add-document-modal/add-document-side-panel';
import { format0 } from 'utils/number';

import {
  DatasetProvider,
  useDatasetConfig,
  useDatasetAtoms,
} from './dataset-context';
import { datasetRegistry } from './datasets';
import { SubsurfaceColorSection } from './subsurface-maps/color-section';
import {
  allDatasetAtoms,
  sidebarCollapsedAtom,
  dividerPositionAtom,
  showMapAtom,
  showTableAtom,
  activeDatasetTabAtom,
  visibleDatasetIdsAtom,
  globalTextAtom,
  setGlobalTextAtom,
  countActiveFilters,
  totalActiveFiltersAtom,
  resetAllFiltersAtom,
  plotByCountyAtom,
  spatialFilterModeAtom,
  type DatasetAtoms,
} from './atoms';
import type { DatasetConfig } from './types';
import { MapSection } from './map-section/map-section';
import {
  PREVIEW_HOST_ID,
  ResultsSection,
} from './results-section/results-section';
import { FilterModal } from './filter-modal/filter-modal';
import { SpatialFilterControls } from './map-section/spatial-filter-controls';
import { SelectedFiltersDisplay } from './map-section/selected-filters-display';
import { LayerColorPickerInline } from './layer-color-picker';
import { ColorBySection, DatasetColorRules } from './color-by-section';
import { DatasetsModal } from './datasets-modal';
import { useMapQueryExport, type MapQueryExport } from './use-map-query-export';
import { MapQueryExportBanner } from './export-status-banner';
import { DatasetLifecycle } from './dataset-lifecycle';
import { MapQuerySpatialExport } from './map-query-spatial-export';
import { MapQueryCreateArea } from './create-area/create-area-host';
import { createAreaOpenAtom } from './create-area/atoms';
import styles from './page.module.scss';

const datasetEntries = Object.entries(datasetRegistry);

// Courthouse is the Map Query default; the dedicated AFE/DO/RC pages own those
// types. Categories/formats come from /configurations (see the modal).
const DOCUMENT_UPLOAD_CONFIG: DocumentUploadConfig = {
  documentType: 'courthouse',
  promptCategory: 'document_metadata',
  accept: ['.pdf', '.tif', '.tiff'],
  uploadHelperText: 'Upload a single PDF, TIF, or TIFF file (max 500 MB limit)',
  promptHelperText: 'For courthouse documents',
  idPrefix: 'map-query-doc-',
  errorFallback: 'Problem uploading document',
};

// ─── "Plot by" control (Documents dataset) ───

const SURVEY_AREAS_NOTE =
  'Documents plotted by their legal survey area geometry — the most precise footprint available. Documents with no survey areas are not plotted.';
const COUNTY_RECORDING_NOTE =
  'Documents matched to the drawn area by their recording county rather than their survey-area centroid. This filters the results list; documents are still plotted on the map by their survey areas (those without one are not plotted).';

/**
 * Radio to match documents against the map extent/AOI by their recording county
 * instead of their survey-area centroid. Writes `plot_by_county` onto this
 * dataset's search payload, which flows to both the list POST and the vector
 * tiles. Disabled without a spatial map filter, since county matching only
 * applies against an extent/AOI. Filter-only: matched documents appear in the
 * list, but documents lacking a survey area still aren't rendered as map dots.
 * @return {JSX.Element} The control.
 */
function PlotByControl() {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const setSearch = useSetAtom(atoms.searchAtom);
  const [plotByCounty, setPlotByCounty] = useAtom(plotByCountyAtom);
  const spatialMode = useAtomValue(spatialFilterModeAtom);
  const disabled = spatialMode === 'none';

  /**
   * Switches between survey-area and county-recording matching.
   * @param {string | number | undefined} value The chosen radio's value.
   */
  const handleChange = (value: string | number | undefined) => {
    const county = value === 'county';
    setPlotByCounty(county);
    setSearch((current) => ({
      ...(current ?? config.defaultPayload),
      page: 1,
      plot_by_county: county ? true : undefined,
    }));
  };

  return (
    <div className={styles.plotBySection}>
      <div className={styles.plotByHeader}>
        <span className={styles.plotByLabel}>Plot by</span>
        <Toggletip align="bottom-start">
          <ToggletipButton label="About document plotting modes">
            <Information />
          </ToggletipButton>
          <ToggletipContent>
            <p>
              <strong>Survey Areas:</strong> {SURVEY_AREAS_NOTE}
            </p>
            <p>
              <strong>County Recording:</strong> {COUNTY_RECORDING_NOTE}
            </p>
          </ToggletipContent>
        </Toggletip>
      </div>
      <RadioButtonGroup
        legendText=""
        name="documents-plot-by"
        valueSelected={plotByCounty ? 'county' : 'survey'}
        onChange={handleChange}
        orientation="vertical"
        disabled={disabled}
      >
        <RadioButton
          id="plot-by-survey"
          labelText="Survey Areas"
          value="survey"
        />
        <RadioButton
          id="plot-by-county"
          labelText="County Recording"
          value="county"
        />
      </RadioButtonGroup>
      <p className={styles.plotByNote}>
        {plotByCounty ? COUNTY_RECORDING_NOTE : SURVEY_AREAS_NOTE}
      </p>
    </div>
  );
}

// ─── Per-Dataset Sidebar Section ───

/**
 * The dataset's eye beside its sidebar header: shows or hides it on the map only, so
 * its table keeps every row. It sits outside the accordion's heading button, so it is
 * a button of its own and never opens or closes the section.
 * @return {JSX.Element} The toggle.
 */
function MapLayerToggle(): JSX.Element {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const [onMap, setOnMap] = useAtom(atoms.mapLayerVisibleAtom);
  return (
    <span className={styles.mapLayerToggle}>
      <IconButton
        kind="ghost"
        size="sm"
        align="left"
        label={`${onMap ? 'Hide' : 'Show'} ${config.label} on map`}
        aria-pressed={!onMap}
        onClick={() => setOnMap(!onMap)}
      >
        {onMap ? <View /> : <ViewOff />}
      </IconButton>
    </span>
  );
}

/**
 * One dataset's sidebar section (collapsed until opened): colour controls, Add
 * Filter and the applied filters. Muted while the dataset is hidden.
 * @return {JSX.Element} The accordion item.
 */
function DatasetSidebarSection() {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const searchResult = useAtomValue(atoms.searchAtom);
  const setFilterModalOpen = useSetAtom(atoms.filterModalOpenAtom);
  const layerColor = useAtomValue(atoms.layerColorAtom);
  const disabledFilters = useAtomValue(atoms.disabledFiltersAtom);
  // Toggled off in the Datasets modal → mute this section (collapse + disable).
  const visible = useAtomValue(atoms.scopedLayerVisibleAtom);
  // Collapsed until opened; open={visible && open} also keeps it shut while hidden.
  const [open, setOpen] = useState(false);

  const { data } = searchResult;

  const filterCount = useMemo(
    () =>
      countActiveFilters(
        searchResult.currentPayload as Record<string, unknown> | undefined,
        config
      ),
    [searchResult.currentPayload, config]
  );

  // Disabled filters keep the panel visible even though they don't count as
  // active, so they can be re-enabled.
  const hasDisabledFilters = useMemo(
    () => Object.values(disabledFilters).some((v) => v !== undefined),
    [disabledFilters]
  );

  return (
    <div className={styles.datasetItem}>
      <AccordionItem
        className={visible ? undefined : styles.datasetSectionMuted}
        disabled={!visible}
        open={visible && open}
        onHeadingClick={({ isOpen }) => setOpen(isOpen)}
        title={
          <span className={styles.accordionTitle}>
            <span
              className={styles.datasetDot}
              style={{ backgroundColor: layerColor }}
            />
            {`${config.label}${
              data?.totalCount !== undefined
                ? ` (${format0(data.totalCount)})`
                : ''
            }`}
          </span>
        }
      >
        <div className={styles.datasetSection}>
          <div className={styles.colorSection}>
            <LayerColorPickerInline label={`${config.label} Default`} />
            {config.rasterLocator ? (
              // A raster's cells are coloured by a ramp, not one layer colour (the
              // Default colour paints its footprints), so its one rule is a ramp card.
              <SubsurfaceColorSection />
            ) : (
              <DatasetColorRules />
            )}
          </div>
          <div className={styles.datasetFilterActions}>
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Add}
              onClick={() => setFilterModalOpen(true)}
            >
              Add Filter
            </Button>
          </div>
          {config.supportsCountyPlot && <PlotByControl />}
          {filterCount === 0 && !hasDisabledFilters ? (
            <p className={styles.emptyFiltersText}>
              No Filters have been added yet
            </p>
          ) : (
            <SelectedFiltersDisplay />
          )}
        </div>
      </AccordionItem>
      {visible && <MapLayerToggle />}
    </div>
  );
}

// ─── Tabbed Results ───

/**
 * The visible datasets' result tabs, mounting only the active tab's results.
 * @param {object} props The tabs.
 * @param {MapQueryExport} props.mapQueryExport The page's CSV export.
 * @return {JSX.Element} The tabs.
 */
function TabbedResults({ mapQueryExport }: { mapQueryExport: MapQueryExport }) {
  const [activeTab, setActiveTab] = useAtom(activeDatasetTabAtom);

  // Restore the tab a ?dataset= link asks for (a document breadcrumb back from a
  // new browser tab, where the in-memory tab atom is at its Wells default). Strip
  // the param after so it doesn't override a later manual tab switch on refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dataset = params.get('dataset');
    if (!dataset || !datasetRegistry[dataset]) return;
    setActiveTab(dataset);
    params.delete('dataset');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`
    );
  }, [setActiveTab]);

  // Per-dataset visibility from the Datasets modal — hide toggled-off tabs.
  const visibleIds = useAtomValue(visibleDatasetIdsAtom);
  const visibleEntries = datasetEntries.filter(([id]) =>
    visibleIds.includes(id)
  );
  const effectiveActive = visibleEntries.some(([id]) => id === activeTab)
    ? activeTab
    : visibleEntries[0]?.[0];
  const activeIndex = Math.max(
    0,
    visibleEntries.findIndex(([id]) => id === effectiveActive)
  );

  return (
    <div className={styles.tabbedResults}>
      <div className={styles.tabBar}>
        <Tabs
          selectedIndex={activeIndex}
          onChange={({ selectedIndex }) => {
            const entry = visibleEntries[selectedIndex];
            if (entry) setActiveTab(entry[0]);
          }}
        >
          <TabList aria-label="Datasets">
            {visibleEntries.map(([id, config]) => (
              <Tab key={id}>
                <DatasetTabLabel config={config} atoms={allDatasetAtoms[id]} />
              </Tab>
            ))}
          </TabList>
        </Tabs>
        <MapQueryExportBanner
          mapQueryExport={mapQueryExport}
          className={styles.exportBanner}
        />
      </div>
      <div className={styles.tabContent}>
        {/*
          Mount ONLY the active tab's ResultsSection. Keeping inactive tables
          mounted-but-`display:none` made them mount at zero width, so the
          shared table's one-shot `useColumnWidths` distribution locked every
          column to its base width and never re-filled once shown (leaving a
          blank gap after the last column). Mounting on activation gives the
          table a real container width so columns fill the row. Search results
          persist in per-dataset jotai atoms (kept subscribed by the sidebar
          and tab buttons), so switching tabs re-renders cached data without a
          refetch. On remount the table re-seeds its (uncontrolled) page index
          from the atom's `currentPayload.page` (see results-section.tsx), so
          the paginator stays in sync with the cached rows; only the table's
          transient scroll position resets.
        */}
        {visibleEntries
          .filter(([id]) => effectiveActive === id)
          .map(([id, config]) => (
            <div
              key={id}
              className={styles.tabPanel}
              style={{ display: 'flex' }}
            >
              <DatasetProvider config={config} atoms={allDatasetAtoms[id]}>
                <ResultsSection mapQueryExport={mapQueryExport} />
              </DatasetProvider>
            </div>
          ))}
      </div>
    </div>
  );
}

/**
 * Tab label: the dataset name with its live record count. Kept as its own
 * component so each tab stays subscribed to its dataset's search results.
 * @param {object} props The label.
 * @param {DatasetConfig} props.config The dataset.
 * @param {DatasetAtoms} props.atoms The dataset's atoms.
 * @return {JSX.Element} The label.
 */
function DatasetTabLabel({
  config,
  atoms,
}: {
  config: DatasetConfig;
  atoms: DatasetAtoms;
}) {
  const { data } = useAtomValue(atoms.searchAtom);
  const Icon = config.icon;
  const label =
    data?.totalCount !== undefined
      ? `${config.label} (${format0(data.totalCount)})`
      : config.label;
  return (
    <span className={styles.tabLabel}>
      {Icon && <Icon />}
      {label}
    </span>
  );
}

// ─── Main Page ───

/**
 * The Map Query page: filters sidebar, map, tabbed results and their modals.
 * @return {JSX.Element} The page.
 */
function MapQueryPageInner() {
  const [dividerPosition, setDividerPosition] = useAtom(dividerPositionAtom);
  const [sidebarCollapsed, setSidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [showMap, setShowMap] = useAtom(showMapAtom);
  const [showTable, setShowTable] = useAtom(showTableAtom);
  const globalText = useAtomValue(globalTextAtom);
  const setGlobalText = useSetAtom(setGlobalTextAtom);
  const totalActiveFilters = useAtomValue(totalActiveFiltersAtom);
  const resetAllFilters = useSetAtom(resetAllFiltersAtom);
  const [isDocumentPanelOpen, setIsDocumentPanelOpen] = useState(false);
  const [isDatasetsModalOpen, setIsDatasetsModalOpen] = useState(false);
  const setCreateAreaOpen = useSetAtom(createAreaOpenAtom);
  // Owned here, not per results section: only the active tab's section mounts,
  // and hiding the table unmounts it, which would lose a running export.
  const mapQueryExport = useMapQueryExport();
  // Bleed the map app to the shell edges (the shared #main-content has 32px
  // padding every other page wants). Scoped by a class so nothing else changes.
  useEffect(() => {
    const el = document.getElementById('main-content');
    el?.classList.add('map-query-fullbleed');
    return () => el?.classList.remove('map-query-fullbleed');
  }, []);

  const handleGlobalSearch = useCallback(
    (value: string | number) => {
      setGlobalText(String(value));
    },
    [setGlobalText]
  );

  return (
    <>
      {/* Cleanup on unmount for each dataset */}
      {datasetEntries.map(([id]) => (
        <DatasetLifecycle key={id} atoms={allDatasetAtoms[id]} resetOnUnmount />
      ))}
      <div className={styles.pageToolbar}>
        {!sidebarCollapsed && (
          <div className={styles.toolbarFiltersHeader}>
            <span className={styles.filtersHeading}>
              {`Map Query (${totalActiveFilters} filter${
                totalActiveFilters === 1 ? '' : 's'
              } applied)`}
            </span>
            <div className={styles.filtersHeaderActions}>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Reset}
                iconDescription="Reset all filters"
                tooltipPosition="bottom"
                // Resets each dataset's discrete filters to defaults, preserving
                // the map extent/AOI, text search, and active tab.
                disabled={totalActiveFilters === 0}
                onClick={() => resetAllFilters()}
              />
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Layers}
                iconDescription="Datasets"
                tooltipPosition="bottom"
                onClick={() => setIsDatasetsModalOpen(true)}
              />
            </div>
          </div>
        )}
        <Button
          kind="ghost"
          size="md"
          className={styles.filterToggleBtn}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          // No aria-label: the visible text (the applied count, or Show/Hide
          // Filters) is the name, as FilterToggle has it.
          renderIcon={sidebarCollapsed ? OpenPanelLeft : OpenPanelFilledLeft}
        >
          {totalActiveFilters > 0
            ? `${totalActiveFilters} Filter${
                totalActiveFilters === 1 ? '' : 's'
              } Applied`
            : sidebarCollapsed
              ? 'Show Filters'
              : 'Hide Filters'}
        </Button>
        <div className={styles.searchWrap}>
          <DebouncedInput
            value={globalText}
            onChange={handleGlobalSearch}
            placeholder="Search"
          />
        </div>
        <div className={styles.toolbarActions}>
          <MapQuerySpatialExport />
          <MenuButton label="Add" kind="primary" size="md">
            <MenuItem
              label="Document"
              onClick={() => setIsDocumentPanelOpen(true)}
            />
            <MenuItem label="Area" onClick={() => setCreateAreaOpen(true)} />
          </MenuButton>
        </div>
      </div>
      <div className={styles.pageLayout}>
        <MapQueryFilterSidebar isOpen={!sidebarCollapsed}>
          <Accordion>
            <AccordionItem title="Map Query Settings" open>
              <div className={styles.settingsStack}>
                <Checkbox
                  id="mq-show-map"
                  labelText="Map"
                  checked={showMap}
                  // Keep at least one pane visible: block hiding the last one.
                  onChange={() => {
                    if (showMap && !showTable) return;
                    setShowMap(!showMap);
                  }}
                />
                <Checkbox
                  id="mq-show-table"
                  labelText="Table"
                  checked={showTable}
                  onChange={() => {
                    if (showTable && !showMap) return;
                    setShowTable(!showTable);
                  }}
                />
              </div>
            </AccordionItem>
            <AccordionItem title="Global Map Filter" open>
              <div className={styles.globalMapFilter}>
                <SpatialFilterControls />
                <ColorBySection />
              </div>
            </AccordionItem>
            {datasetEntries.map(([id, config]) => (
              <DatasetProvider
                key={id}
                config={config}
                atoms={allDatasetAtoms[id]}
              >
                <DatasetSidebarSection />
              </DatasetProvider>
            ))}
          </Accordion>
        </MapQueryFilterSidebar>
        <div className={styles.mainContent} id={PREVIEW_HOST_ID}>
          <SearchPageLayout
            mapSection={<MapSection />}
            resultsSection={<TabbedResults mapQueryExport={mapQueryExport} />}
            mapHeightPercent={dividerPosition}
            divider={
              <DraggableDivider
                onDrag={setDividerPosition}
                value={dividerPosition}
                label="Map and results split"
              />
            }
            showMap={showMap}
            showTable={showTable}
          />
          {/* The tab-line banner unmounts with the table, so float it over the map. */}
          {!showTable && (
            <div className={styles.floatingExportBanner}>
              <MapQueryExportBanner
                mapQueryExport={mapQueryExport}
                className={styles.exportBanner}
              />
            </div>
          )}
        </div>
      </div>
      {/* One filter modal per dataset */}
      {datasetEntries.map(([id, config]) => (
        <DatasetProvider key={id} config={config} atoms={allDatasetAtoms[id]}>
          <FilterModal />
        </DatasetProvider>
      ))}
      <AddDocumentSidePanel
        open={isDocumentPanelOpen}
        onClose={() => setIsDocumentPanelOpen(false)}
        title="Add Document"
        subtitle="Upload a single document and choose the models used to provision it."
        config={DOCUMENT_UPLOAD_CONFIG}
        showDocumentTypeSelector
      />
      <MapQueryCreateArea />
      <DatasetsModal
        open={isDatasetsModalOpen}
        onClose={() => setIsDatasetsModalOpen(false)}
      />
    </>
  );
}

/**
 * The Map Query page.
 * @return {JSX.Element} The page.
 */
export function MapQueryPage() {
  return <MapQueryPageInner />;
}
